// src/pages/AnnonceDetailPage.js
// Page détail d'une annonce — route publique (acquisition SEO)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Megaphone,
  MapPin,
  Eye,
  MessageCircle,
  Pencil,
  Trash2,
  Flag,
  Star,
  Zap,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { Btn, Badge, Loading, Empty, ErrorState, ConfirmModal } from '../components/ui';
import { getAnnonceCategoryLabel } from '../constants';
import { formatPrice, timeAgo } from '../utils';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import annonceService from '../services/annonceService';
import userService from '../services/userService';
import ReportModal from '../modals/ReportModal';
import ProfileModal from '../modals/ProfileModal';
import AnnonceFormModal from '../modals/AnnonceFormModal';

// ── Helpers ──

const CATEGORY_COLORS = {
  commerce: 'indigo',
  services: 'emerald',
  logement: 'amber',
  emploi: 'violet',
  divers: 'gray',
};

// ── Lightbox galerie ──

const ImageGalleryLightbox = ({ photos, initialIndex, onClose }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);
  const touchStartX = useRef(null);

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(photos.length - 1, i + 1)),
    [photos.length]
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, goPrev, goNext]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta > 50) goPrev();
    if (delta < -50) goNext();
  };

  return (
    <div
      data-testid="annonce-lightbox"
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <div className="flex-shrink-0 flex justify-end p-4">
        <button
          data-testid="annonce-lightbox-close"
          onClick={onClose}
          aria-label={t('annonceDetail.lightbox.close')}
          className="p-2 text-white/70 hover:text-white transition"
        >
          <X size={28} />
        </button>
      </div>

      {/* Image centrale */}
      <div
        className="flex-1 flex items-center justify-center relative min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        {photos.length > 1 && index > 0 && (
          <button
            data-testid="annonce-lightbox-prev"
            onClick={goPrev}
            aria-label={t('annonceDetail.lightbox.prevPhoto')}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        <img
          src={photos[index]}
          alt={t('annonceDetail.lightbox.photoOf', { current: index + 1, total: photos.length })}
          className="max-w-full max-h-full object-contain p-4"
        />
        {photos.length > 1 && index < photos.length - 1 && (
          <button
            data-testid="annonce-lightbox-next"
            onClick={goNext}
            aria-label={t('annonceDetail.lightbox.nextPhoto')}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {/* Footer : indicateur + thumbnails */}
      <div
        className="flex-shrink-0 pb-6 pt-3 flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white/80 text-sm font-medium">
          {index + 1} / {photos.length}
        </span>
        {photos.length > 1 && (
          <div className="flex gap-2 overflow-x-auto px-4 max-w-full">
            {photos.map((url, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={t('annonceDetail.lightbox.thumbnail', { index: i + 1 })}
                className={`w-16 h-16 rounded flex-shrink-0 overflow-hidden transition ${
                  i === index ? 'ring-2 ring-white opacity-100' : 'opacity-50 hover:opacity-75'
                }`}
              >
                <img
                  src={url}
                  alt={t('annonceDetail.lightbox.thumbnail', { index: i + 1 })}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Composant ──

const AnnonceDetailPage = ({ user, openAuth, toast }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [annonce, setAnnonce] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [contacting, setContacting] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const viewsIncremented = useRef(false);

  const FEATURED_BADGES = {
    une: { label: t('annonces.featured.une'), icon: Star, className: 'bg-amber-500 text-white' },
    urgent: { label: t('annonces.featured.urgent'), icon: Zap, className: 'bg-red-500 text-white' },
  };

  // Fetch annonce
  const fetchAnnonce = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);
      const result = await annonceService.getAnnonce(id);
      if (result.success) {
        setAnnonce(result.data);
      } else {
        setError(true);
      }
    } catch (_err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAnnonce();
  }, [fetchAnnonce]);

  // Increment views (une seule fois)
  useEffect(() => {
    if (!id || viewsIncremented.current) return;
    viewsIncremented.current = true;
    annonceService.incrementViews(id);
  }, [id]);

  // Fetch author profile
  useEffect(() => {
    if (!annonce?.authorId) return;
    userService.getProfile(annonce.authorId).then((profile) => {
      if (profile) setAuthor(profile);
    });
  }, [annonce?.authorId]);

  // ── Actions ──

  const handleContact = async () => {
    if (!user) {
      openAuth();
      return;
    }
    setContacting(true);
    try {
      const contactAnnonceurFn = httpsCallable(functions, 'contactAnnonceur');
      const result = await contactAnnonceurFn({ annonceId: id });
      if (result.data.success) {
        navigate(`/messages/${result.data.conversationId}`);
        if (result.data.existing) {
          toast(t('annonceDetail.toast.existingConversation'), 'info');
        } else {
          toast(t('annonceDetail.toast.conversationCreated'), 'success');
        }
      }
    } catch (err) {
      toast(err.message || t('annonceDetail.toast.contactError'), 'error');
    } finally {
      setContacting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const result = await annonceService.deleteAnnonce(id, user.uid);
    setDeleting(false);
    setShowDeleteConfirm(false);
    if (result.success) {
      toast(t('annonceDetail.toast.deleted'), 'success');
      navigate('/annonces');
    } else {
      toast(result.error || t('annonceDetail.toast.deleteError'), 'error');
    }
  };

  const handleReport = () => {
    if (!user) {
      openAuth();
      return;
    }
    setShowReport(true);
  };

  // Gallery navigation
  const photos = annonce?.photos || [];
  const hasPhotos = photos.length > 0;
  const canGoPrev = galleryIndex > 0;
  const canGoNext = galleryIndex < photos.length - 1;

  // ── États de chargement / erreur ──

  if (loading) return <Loading msg={t('annonceDetail.loading')} />;

  if (error) {
    return (
      <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <ErrorState onRetry={fetchAnnonce} />
      </div>
    );
  }

  if (!annonce) {
    return (
      <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <Empty
          icon={Megaphone}
          title={t('annonceDetail.notFound.title')}
          desc={t('annonceDetail.notFound.desc')}
          action={
            <Btn v="outline" icon={ArrowLeft} onClick={() => navigate('/annonces')}>
              {t('annonceDetail.backToAnnonces')}
            </Btn>
          }
        />
      </div>
    );
  }

  // ── Dérivés ──

  const isOwner = user && annonce.authorId === user.uid;
  const categoryColor = CATEGORY_COLORS[annonce.category] || 'gray';
  const featuredBadge = annonce.featured ? FEATURED_BADGES[annonce.featured.type] : null;

  const displayPrice = () => {
    if (annonce.priceLabel === 'gratuit') return t('common.free');
    if (annonce.priceLabel === 'sur_devis') return t('common.onQuote');
    return formatPrice(annonce.price);
  };

  const viewCount = annonce.views || 0;
  const contactCount = annonce.contactCount || 0;

  const pageTitle = `${annonce.title || 'Annonce'} — HmongHearts`;
  const pageDesc =
    (annonce.description || '').slice(0, 150) || 'Annonce sur HmongHearts, la communauté Hmong.';
  const ogImage = annonce.photos?.[0] || 'https://hmonghearts.com/og-default.png';

  const annonceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: annonce.title,
    description: annonce.description || '',
    image: annonce.photos?.[0] || 'https://hmonghearts.com/og-default.png',
    offers: {
      '@type': 'Offer',
      price: annonce.price || 0,
      priceCurrency: 'EUR',
      availability:
        annonce.status === 'active'
          ? 'https://schema.org/InStock'
          : 'https://schema.org/Discontinued',
      url: `https://hmonghearts.com/annonces/${id}`,
      seller: {
        '@type': 'Organization',
        name: 'HmongHearts',
      },
    },
    category: annonce.category || '',
  };

  return (
    <article className="filigrane-kotaw pt-24 pb-32 lg:pb-20 container mx-auto px-4">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://hmonghearts.com/annonces/${id}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={`https://hmonghearts.com/annonces/${id}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(annonceJsonLd)}</script>
      </Helmet>
      <div className="max-w-3xl mx-auto">
        {/* Retour */}
        <button
          onClick={() => navigate('/annonces')}
          className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition font-medium text-sm"
          data-testid="annonce-btn-back"
        >
          <ArrowLeft size={18} />
          {t('annonceDetail.backToAnnonces')}
        </button>

        {/* Galerie photos */}
        <div className="relative rounded-xl overflow-hidden mb-6">
          {hasPhotos ? (
            <>
              <img
                src={photos[galleryIndex]}
                alt={`${annonce.title} — ${galleryIndex + 1}`}
                className="w-full h-56 sm:h-72 object-cover cursor-pointer"
                onClick={() => setLightboxOpen(true)}
                fetchpriority={galleryIndex === 0 ? 'high' : undefined}
              />
              {photos.length > 1 && (
                <>
                  {canGoPrev && (
                    <button
                      onClick={() => setGalleryIndex((i) => i - 1)}
                      className="absolute left-1 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition"
                      aria-label={t('annonceDetail.lightbox.prevPhoto')}
                      data-testid="annonce-gallery-prev"
                    >
                      <ChevronLeft size={20} />
                    </button>
                  )}
                  {canGoNext && (
                    <button
                      onClick={() => setGalleryIndex((i) => i + 1)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition"
                      aria-label={t('annonceDetail.lightbox.nextPhoto')}
                      data-testid="annonce-gallery-next"
                    >
                      <ChevronRight size={20} />
                    </button>
                  )}
                  {/* Dots */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {photos.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setGalleryIndex(i)}
                        className={`w-3 h-3 rounded-full transition ${i === galleryIndex ? 'bg-white' : 'bg-white/50'}`}
                        style={{ padding: 8, backgroundClip: 'content-box' }}
                        aria-label={t('annonceDetail.lightbox.photo', { index: i + 1 })}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="h-56 sm:h-72 bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center">
              <Megaphone size={64} className="text-white/20" />
            </div>
          )}
        </div>

        {/* Titre + badges */}
        <div className="flex flex-wrap items-start gap-2 mb-3">
          <h1
            className="text-lg sm:text-2xl font-bold dark:text-gray-100 flex-1"
            data-testid="annonce-title"
          >
            {annonce.title}
          </h1>
          <Badge c={categoryColor}>{t(getAnnonceCategoryLabel(annonce.category))}</Badge>
          {featuredBadge && (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${featuredBadge.className}`}
              data-testid="annonce-featured-badge"
            >
              <featuredBadge.icon size={12} />
              {featuredBadge.label}
            </span>
          )}
        </div>

        {/* Prix + ville */}
        <div className="flex items-center gap-4 mb-5">
          <span
            className="text-lg font-bold text-indigo-600 dark:text-indigo-400"
            data-testid="annonce-price"
          >
            {displayPrice()}
          </span>
          <span className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <MapPin size={14} />
            {annonce.city}
          </span>
        </div>

        {/* Description */}
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold mb-3 dark:text-gray-100">
            {t('annonceDetail.description')}
          </h2>
          <p
            className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed"
            data-testid="annonce-description"
          >
            {annonce.description}
          </p>
        </div>

        {/* Annonceur */}
        {author && (
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-bold mb-3 dark:text-gray-100">
              {t('annonceDetail.advertiser')}
            </h2>
            <button
              onClick={() => setShowProfileModal(true)}
              className="flex items-center gap-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800 -mx-2 px-2 py-2 rounded-lg transition"
              data-testid="annonce-author-profile"
            >
              {author.profilePicture ? (
                <img
                  src={author.profilePicture}
                  alt={author.name}
                  className="w-10 h-10 rounded-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold">
                  {(author.name || '?')[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold dark:text-gray-100">{author.name}</p>
                {author.clan && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 font-medium">
                    {author.clan}
                  </span>
                )}
              </div>
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400 mb-6">
          <span className="flex items-center gap-1.5" data-testid="annonce-views">
            <Eye size={15} />
            {viewCount !== 1
              ? t('annonceDetail.views_plural', { count: viewCount })
              : t('annonceDetail.views', { count: viewCount })}
          </span>
          <span className="flex items-center gap-1.5" data-testid="annonce-contacts">
            <MessageCircle size={15} />
            {contactCount !== 1
              ? t('annonceDetail.contacts_plural', { count: contactCount })
              : t('annonceDetail.contacts', { count: contactCount })}
          </span>
          {annonce.createdAt && <span className="text-xs">{timeAgo(annonce.createdAt)}</span>}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {!isOwner && (
            <Btn
              icon={MessageCircle}
              onClick={handleContact}
              disabled={contacting}
              className="flex-1 text-sm py-2.5"
              data-testid="annonce-btn-contacter"
            >
              {contacting ? (
                <span data-testid="annonce-contact-loading">{t('common.connecting')}</span>
              ) : (
                t('annonceDetail.actions.contact')
              )}
            </Btn>
          )}

          {isOwner && (
            <>
              <Btn
                v="outline"
                icon={Pencil}
                onClick={() => setFormOpen(true)}
                className="flex-1"
                data-testid="annonce-btn-modifier"
              >
                {t('annonceDetail.actions.edit')}
              </Btn>
              <Btn
                v="ghost"
                icon={Trash2}
                onClick={() => setShowDeleteConfirm(true)}
                className="flex-1 text-red-500 hover:text-red-600"
                data-testid="annonce-btn-supprimer"
              >
                {t('annonceDetail.actions.delete')}
              </Btn>
            </>
          )}

          {!isOwner && user && (
            <Btn
              v="ghost"
              icon={Flag}
              onClick={handleReport}
              className="text-gray-500 dark:text-gray-400"
              data-testid="annonce-btn-signaler"
            >
              {t('annonceDetail.actions.report')}
            </Btn>
          )}
        </div>
      </div>

      {/* Modal suppression */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('annonceDetail.deleteConfirm.title')}
        message={t('annonceDetail.deleteConfirm.message', { title: annonce.title })}
        confirmLabel={t('annonceDetail.actions.delete')}
        confirmVariant="danger"
        loading={deleting}
      />

      {/* Modal signalement */}
      {showReport && user && (
        <ReportModal
          isOpen={showReport}
          onClose={() => setShowReport(false)}
          currentUserId={user.uid}
          reportedId={annonce.authorId}
          type="annonce"
          annonceId={annonce.id}
          toast={toast}
        />
      )}

      {/* Modal profil annonceur */}
      {showProfileModal && author && (
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          profile={author}
          toast={toast}
          currentUser={user}
        />
      )}

      {/* Modal édition */}
      <AnnonceFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        user={user}
        toast={toast}
        annonce={annonce}
        onSuccess={fetchAnnonce}
      />

      {/* Lightbox galerie */}
      {lightboxOpen && hasPhotos && (
        <ImageGalleryLightbox
          photos={photos}
          initialIndex={galleryIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </article>
  );
};

export default AnnonceDetailPage;
